import { useRef, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { EmployeesPersonalDataDocument } from 'generated/resources/graphql'
import { groupBy } from 'lodash'
import { observable } from 'mobx'

import { observer } from 'mobx-react'

import { PortalWrapper } from 'modules/core/components/PortalWrapper'
import { WhatsAppButton } from 'modules/core/components/WhatsAppButton'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import legalEntities from 'modules/core/store/collections/legalEntities'
import { employeeUser } from 'modules/core/store/collections/users'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import { ContractCuesProvider } from 'modules/contracts/lib/contractCuesContext'
import ContractCuesBanner from 'modules/contracts/pages/ContractVersionsPage/Id/ContractCuesBanner'
import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import CustomFieldsBlock from 'modules/custom_resources/components/CustomFields/CustomFieldsBlock'
import CustomField from 'modules/custom_resources/store/models/customFields/Field'
import BankInfo from 'modules/employees/components/blocks/BankInfo'
import Contact from 'modules/employees/components/blocks/Contact'
import EmployeeAddress from 'modules/employees/components/blocks/EmployeeAddress'
import EmployeeIdentification from 'modules/employees/components/blocks/EmployeeIdentification'
import { EmployeePersonalProvider } from 'modules/employees/components/blocks/EmployeePersonalProvider'
import EmployeeRetirementRegimeApplied from 'modules/employees/components/blocks/EmployeeRetirementRegime'
import EmployeeStatusBanner from 'modules/employees/components/blocks/EmployeeStatusBanner'
import FamilySituation from 'modules/employees/components/blocks/FamilySituation'
import General from 'modules/employees/components/blocks/General'
import Irpf from 'modules/employees/components/blocks/Irpf'
import { PayrollEmployeeData } from 'modules/employees/components/blocks/PayrollEmployee'
import { getPayrollEmployeeCategories } from 'modules/employees/components/blocks/PayrollEmployee/categories'
import PersonalSituation from 'modules/employees/components/blocks/PersonalSituation'
import TaxResidence from 'modules/employees/components/blocks/TaxResidence'
import useEmployeeIncidenceRules from 'modules/employees/hooks/useEmployeeIncidenceRules'
import type { CommonRouteContext } from 'modules/employees/lib/types'
import PrimoboxStatusRow from 'modules/integrations_primobox/components/StatusRow'
import Page from 'modules/layout/components/Page'
import Header from 'modules/layout/components/Page/Header'

import Actions from '../Actions'
import PayrollBlock from './_payroll'
import { legalEntityCustomFieldFilter } from './legalEntityCustomFieldFilter'

interface Props {
  contractVersions: ContractVersions
}

const Personal = ({ contractVersions }: Props) => {
  const context = useOutletContext<CommonRouteContext>()
  const {
    onboardingProcess,
    loadingOnboardingProcess,
    cardEmployeeRight,
    headerTitle,
    employee,
    canSeePersonalInfo,
    canEditPersonalInfo,
  } = context

  const [legalEntity, setLegalEntity] = useState<LegalEntity | null>(null)
  const readonly = !canEditPersonalInfo
  const isEligibleForPayroll = employee.isEligibleForPayroll
  const isSpanish = employee.attributes.nationality === 'es'

  const employeeId = employee.attributes.id

  const { company } = useCurrentSession()

  const { data: employeeData } = useQuery(EmployeesPersonalDataDocument, {
    variables: {
      companyId: company.id,
      employeeIds: [employeeId],
      country: legalEntity?.country || '',
    },
  })
  const features = employeeData?.features.featuresConnection.nodes ?? []
  const employeePayrollDataSchema = employeeData?.dataDispatcher.dataSchemasConnection.nodes ?? []
  const employeePayrollData = employeeData?.payrollEmployees.employeeDataConnection.nodes ?? []
  const employeePayrollDatum =
    employeePayrollData.find((data) => data.employeeId === employeeId)?.fields ?? []
  const groupedSchemaFields = groupBy(employeePayrollDataSchema, 'category')

  const hasFeature = (featureName: string) => {
    return features.find((feature) => feature.name === featureName)?.enabled || false
  }

  const hasCoffreFort = hasFeature('coffre_fort')
  const hasPrimobox = hasFeature('integrations_primobox_active')
  const primoboxEmployeeStatus =
    employeeData?.integrationsPrimobox?.employeeStatusesConnection?.nodes[0]

  const { professionalsActivated, hiringIncidence, incidenceRules } =
    useEmployeeIncidenceRules(employeeId)

  const hasCues =
    (!readonly && professionalsActivated && hiringIncidence && incidenceRules?.length > 0) || false

  const listOfFields = useRef(observable.map(new Map())).current
  const belongsToGermanLegalEntity = legalEntity?.country === 'de'

  useAsyncData(async () => {
    const employeeLegalEntity: LegalEntity[] = await legalEntities.fetch({
      data: {
        employee_id: employee.id,
      },
      remove: false, // Don't remove the other LEs from the global collection
    })

    if (Array.isArray(employeeLegalEntity)) {
      setLegalEntity(employeeLegalEntity[0] || null)
    }
  }, [employee.attributes.id])
  return (
    <ContractCuesProvider
      collection={{
        hasCues: hasCues,
        incidenceRules: incidenceRules,
        listOfFields: listOfFields,
      }}
    >
      <EmployeePersonalProvider employee={employee}>
        <Page.Body size="narrow">
          <PortalWrapper container={cardEmployeeRight}>
            <WhatsAppButton employee={employee} canSeePersonalInfo={canSeePersonalInfo} />
            <Actions employee={employee} contractVersions={contractVersions} />
          </PortalWrapper>
          <PortalWrapper container={headerTitle}>
            <Header.Title>{i18n.t('profile.personal_details')}</Header.Title>
          </PortalWrapper>
          {hasCues && (
            <ContractCuesBanner
              title={i18n.t('employee_form.cues_banner.title', {
                employeeName: employeeUser(employee).firstName,
              })}
              employeeName={employeeUser(employee).firstName}
            ></ContractCuesBanner>
          )}
          {!loadingOnboardingProcess && (
            <Box paddingBottom="s32">
              <EmployeeStatusBanner employee={employee} onboardingProcess={onboardingProcess} />
            </Box>
          )}
          <General
            employee={employee}
            readonly={readonly}
            payroll={isEligibleForPayroll}
            legalEntity={legalEntity}
          />
          {belongsToGermanLegalEntity && (
            <PayrollEmployeeData
              employeeId={employeeId}
              companyId={company.id}
              employeePayrollDataSchema={groupedSchemaFields['general_information'] || []}
              employeePayrollDatum={employeePayrollDatum}
              illustration="user-male-female"
              title={i18n.t('employee_form.payroll_data.categories.general_information.title')}
              description={i18n.t(
                'employee_form.payroll_data.categories.general_information.description'
              )}
            />
          )}
          <EmployeeIdentification
            employee={employee}
            legalEntity={legalEntity}
            readonly={readonly}
            payroll={isEligibleForPayroll}
          />
          <EmployeeRetirementRegimeApplied employeeId={employee.id} readonly={readonly} />
          <EmployeeAddress employee={employee} readonly={readonly} />
          <PersonalSituation employee={employee} readonly={readonly} />
          <FamilySituation employee={employee} readonly={readonly} legalEntity={legalEntity} />
          {isEligibleForPayroll && <Irpf employee={employee} />}
          <TaxResidence employee={employee} isSpanish={isSpanish} legalEntity={legalEntity} />
          <Contact employee={employee} readonly={readonly} />
          <BankInfo
            employee={employee}
            canEditPersonalInfo={canEditPersonalInfo}
            canSeeBankInfo={canSeePersonalInfo}
          />
          {belongsToGermanLegalEntity &&
            getPayrollEmployeeCategories().map((category) => (
              <PayrollEmployeeData
                key={`${category.name}-employee-data`}
                employeeId={employeeId}
                companyId={company.id}
                employeePayrollDataSchema={groupedSchemaFields[category.name] || []}
                employeePayrollDatum={employeePayrollDatum}
                illustration={category.illustration}
                title={category.title}
                description={category.description}
              />
            ))}
          {hasFeature('display_new_payroll') && legalEntity && (
            <>
              <PayrollBlock
                employee={employee}
                readonly={readonly}
                companyId={company.id}
                legalEntity={legalEntity}
                hasCoffreFort={hasCoffreFort}
                contractVersions={contractVersions}
              />
              {hasPrimobox && primoboxEmployeeStatus && (
                <PrimoboxStatusRow
                  cardTitle={i18n.t('integrations_primobox.status_card.employee.title')}
                  status={primoboxEmployeeStatus}
                />
              )}
              <CustomFieldsBlock
                usageGroups={[CustomField.UsageGroups.basicInformation]}
                fieldableModelRef="company"
                fieldableId={company.id}
                valuableModelRef="employee"
                valuableInstance={employee}
                icon="building"
                title={i18n.t('employee_form.custom_fields.basic_information.title')}
                description={i18n.t('employee_form.custom_fields.basic_information.description')}
                employee={employee}
                readonly={readonly}
              />
              <CustomFieldsBlock
                usageGroups={[CustomField.UsageGroups.identificationNumber]}
                fieldableModelRef="company"
                fieldableId={company.id}
                valuableModelRef="employee"
                valuableInstance={employee}
                icon="building"
                title={i18n.t('employee_form.custom_fields.identification_number.title')}
                description={i18n.t(
                  'employee_form.custom_fields.identification_number.description'
                )}
                employee={employee}
                readonly={readonly}
                resourceFieldFilter={legalEntityCustomFieldFilter(legalEntity)}
              />
            </>
          )}
        </Page.Body>
      </EmployeePersonalProvider>
    </ContractCuesProvider>
  )
}

export default observer(Personal)
